import {useQuery} from '@tanstack/react-query';
import {Box, CircularProgress, Alert} from '@mui/material';
import FruitoriSection from './sections/FruitoriSection';
import TargetSection from './sections/TargetSection';
import CollaborazioniSection from './sections/CollaborazioniSection';
import {IDashboardData, SurveySlug} from '../../utils/types';
import {buildApiKey, serializeSlug} from '../../utils/helpers';
import apiCall from '../../../../utils/api';
import {buildFruitoriSlug, buildTargetSlugs, buildCollaborazioniSlugs} from './utils/slugs';

interface Props {
    container: string;
    uoaId: string;
    inizialeSurveyId: string;
    aggiornamentoSurveyId: string;
    finaleSurveyId: string;
    ageGroupQuestion?: string;
}

const fetchSlugs = async (
    container: string,
    uoaId: string,
    slugs: SurveySlug[],
): Promise<IDashboardData> => {
    const res = await apiCall({
        url: `containers/${container}/uoa/${uoaId}/dashboards/fsv`,
        method: 'POST',
        data: {
            question_slugs: slugs.map(serializeSlug),
        },
    });

    const result = {} as IDashboardData;
    for (const slug of slugs) {
        const apiKey = buildApiKey(slug);
        result[slug.key] = res.data[apiKey] ?? {uoa: 0, average: 0};
    }
    return result;
};

const Dashboard2 = ({
    container,
    uoaId,
    aggiornamentoSurveyId,
    finaleSurveyId,
    inizialeSurveyId,
    ageGroupQuestion = 'question9',
}: Props) => {
    const {data, isLoading, isError} = useQuery({
        queryKey: [
            'luoghi-dashboard2',
            container,
            uoaId,
            inizialeSurveyId,
            aggiornamentoSurveyId,
            finaleSurveyId,
        ],
        queryFn: async () => {
            const slugs = [
                buildFruitoriSlug(
                    Number(finaleSurveyId),
                    Number(aggiornamentoSurveyId),
                    Number(inizialeSurveyId),
                ),
                ...buildTargetSlugs(
                    Number(aggiornamentoSurveyId),
                    Number(inizialeSurveyId),
                    ageGroupQuestion,
                ),
                ...buildCollaborazioniSlugs(Number(inizialeSurveyId)),
            ];

            return fetchSlugs(container, uoaId, slugs);
        },
        staleTime: 5 * 60 * 1000,
    });

    if (isLoading) {
        return (
            <Box sx={{display: 'flex', justifyContent: 'center', py: 8}}>
                <CircularProgress size={48} />
            </Box>
        );
    }

    if (isError || !data) {
        return <Alert severity='error'>Errore nel caricamento dei dati.</Alert>;
    }

    return (
        <Box>
            <FruitoriSection data={data['fruitori']} />
            <TargetSection data={data} />
            <CollaborazioniSection data={data} />
        </Box>
    );
};

export default Dashboard2;
